import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

export interface ShrinkOptions {
  /** Pages whose individual byte-size exceeds this threshold get rasterized.
   *  Pages below it are copied verbatim — preserving vector text quality. */
  rasterizeThresholdBytes: number;
  /** DPI used when rasterizing a page. 150 is a sweet spot for highlighted scans. */
  rasterDpi: number;
  /** JPEG quality 0–1 for rasterized pages. */
  jpegQuality: number;
  /** Force rasterizing every page (turns the heuristic off — useful when input is
   *  uniformly tiny vector text but the user reports macOS-invisible highlights). */
  forceRasterizeAll?: boolean;
}

export const PRESETS = {
  tournament: {
    label: "TOURNAMENT",
    sublabel: "Submission-ready, well under 100MB",
    rasterizeThresholdBytes: 200_000,
    rasterDpi: 110,
    jpegQuality: 0.72,
  },
  email: {
    label: "EMAIL",
    sublabel: "Aggressively small",
    rasterizeThresholdBytes: 120_000,
    rasterDpi: 85,
    jpegQuality: 0.62,
  },
  tiny: {
    label: "TINY",
    sublabel: "Maximum compression",
    rasterizeThresholdBytes: 80_000,
    rasterDpi: 70,
    jpegQuality: 0.55,
  },
} as const satisfies Record<string, ShrinkOptions & { label: string; sublabel: string }>;

/** Hard cap on rendered canvas long-edge. Phone-scanned PDFs sometimes embed
 *  giant pages (e.g. 30+ inch user-space dimensions) which would balloon the
 *  output even at modest DPI. Cap prevents pathological cases. */
const MAX_RENDER_LONG_EDGE_PX = 2200;

export type PresetKey = keyof typeof PRESETS;

export interface PageReport {
  pageIndex: number;
  originalBytes: number;
  rasterized: boolean;
  finalBytes: number;
}

export interface ShrinkProgress {
  phase: "analyzing" | "rasterizing" | "assembling" | "done";
  pageIndex: number;
  totalPages: number;
  message: string;
}

export interface ShrinkResult {
  bytes: Uint8Array;
  originalSize: number;
  finalSize: number;
  pages: PageReport[];
}

const PDF_USER_UNIT_PER_INCH = 72;

/** Extract a single page into its own one-page PDF and return its serialized
 *  byte length. This gives us a cheap heuristic for how "fat" a page is. */
async function measurePageBytes(src: PDFDocument, pageIndex: number): Promise<number> {
  const single = await PDFDocument.create();
  const [copied] = await single.copyPages(src, [pageIndex]);
  single.addPage(copied);
  const bytes = await single.save({ useObjectStreams: false });
  return bytes.length;
}

/** Render a page with pdf.js to a JPEG data URL at the requested DPI. */
async function rasterizePage(
  pdfjsDoc: pdfjsLib.PDFDocumentProxy,
  pageIndex: number,
  dpi: number,
  jpegQuality: number,
): Promise<{ jpeg: Uint8Array; widthPt: number; heightPt: number }> {
  const page = await pdfjsDoc.getPage(pageIndex + 1);
  const naturalViewport = page.getViewport({ scale: 1 });
  const desiredScale = dpi / PDF_USER_UNIT_PER_INCH;
  const longEdge = Math.max(naturalViewport.width, naturalViewport.height);
  const cappedScale = MAX_RENDER_LONG_EDGE_PX / longEdge;
  const scale = Math.min(desiredScale, cappedScale);
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const ctx = canvas.getContext("2d", { alpha: false });
  if (!ctx) throw new Error("Could not get 2D context");

  // White fill so transparency in source PDFs doesn't bleed black through JPEG.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  await page.render({ canvasContext: ctx, viewport }).promise;

  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
      "image/jpeg",
      jpegQuality,
    );
  });
  const jpeg = new Uint8Array(await blob.arrayBuffer());

  return {
    jpeg,
    widthPt: naturalViewport.width,
    heightPt: naturalViewport.height,
  };
}

export async function shrinkPdf(
  input: Uint8Array,
  options: ShrinkOptions,
  onProgress?: (p: ShrinkProgress) => void,
): Promise<ShrinkResult> {
  const originalSize = input.length;

  // pdf-lib will mutate buffers it ingests; pass a copy so we can hand the
  // original off to pdf.js separately.
  const srcDoc = await PDFDocument.load(input.slice(), { ignoreEncryption: true });
  const totalPages = srcDoc.getPageCount();

  const pdfjsDoc = await pdfjsLib.getDocument({ data: input.slice() }).promise;

  const out = await PDFDocument.create();
  const reports: PageReport[] = [];

  for (let i = 0; i < totalPages; i++) {
    onProgress?.({
      phase: "analyzing",
      pageIndex: i,
      totalPages,
      message: `Analyzing page ${i + 1} of ${totalPages}`,
    });

    const originalBytes = await measurePageBytes(srcDoc, i);
    const shouldRasterize =
      options.forceRasterizeAll === true ||
      originalBytes > options.rasterizeThresholdBytes;

    if (shouldRasterize) {
      onProgress?.({
        phase: "rasterizing",
        pageIndex: i,
        totalPages,
        message: `Rasterizing page ${i + 1} of ${totalPages}`,
      });
      const { jpeg, widthPt, heightPt } = await rasterizePage(
        pdfjsDoc,
        i,
        options.rasterDpi,
        options.jpegQuality,
      );
      const embedded = await out.embedJpg(jpeg);
      const page = out.addPage([widthPt, heightPt]);
      page.drawImage(embedded, { x: 0, y: 0, width: widthPt, height: heightPt });
      reports.push({
        pageIndex: i,
        originalBytes,
        rasterized: true,
        finalBytes: jpeg.length,
      });
    } else {
      const [copied] = await out.copyPages(srcDoc, [i]);
      out.addPage(copied);
      reports.push({
        pageIndex: i,
        originalBytes,
        rasterized: false,
        finalBytes: originalBytes,
      });
    }
  }

  onProgress?.({
    phase: "assembling",
    pageIndex: totalPages,
    totalPages,
    message: "Assembling final document",
  });

  const bytes = await out.save({ useObjectStreams: true });

  await pdfjsDoc.destroy();

  onProgress?.({
    phase: "done",
    pageIndex: totalPages,
    totalPages,
    message: "Compression complete",
  });

  return {
    bytes,
    originalSize,
    finalSize: bytes.length,
    pages: reports,
  };
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
